/**
 * Phase 4 — Reports Broker (D4.1)
 *
 * Handles all API calls for the report catalog:
 *  - Fetch the report type catalog
 *  - List generated report files
 *  - Generate a report (PDF + XLSX)
 *  - Download a report file
 */
import AxiosManager from '@/utils/AxiosManager'
import Constants from '@/helpers/Constants'

export interface ReportFilter {
  report_type: string
  billing_period_id?: number | null
  date_from?: string | null
  date_to?: string | null
  tag_ids?: number[] | null
  sort_by?: string | null
  sort_order?: 'asc' | 'desc'
  audit_sub_type?: string
  format?: 'pdf' | 'xlsx' | 'both'
}

export interface GenerateReportResult {
  report_type: string
  title: string
  subtitle: string
  row_count: number
  pdf_file: string | null
  xlsx_file: string | null
  summary: Record<string, any>
}

export default class ReportsBroker {
  private static get ax(): AxiosManager {
    return new AxiosManager(Constants.SERVER_URL)
  }

  /**
   * Fetch the list of available report types from the server.
   */
  public static async getCatalog(): Promise<any[]> {
    const res = await ReportsBroker.ax.get('/report_catalog')
    return res ?? []
  }

  /**
   * List previously generated report files.
   */
  public static async listReports(): Promise<any[]> {
    const res = await ReportsBroker.ax.get('/list_reports')
    return res ?? []
  }

  /**
   * Run a report and produce PDF + XLSX output files.
   */
  public static async generateReport(filter: ReportFilter): Promise<GenerateReportResult | null> {
    const res = await ReportsBroker.ax.post('/generate_report', filter)
    return res ?? null
  }

  /**
   * Return the URL for downloading a report file (PDF or XLSX).
   * Uses a direct URL so the browser opens a download dialog.
   */
  public static downloadUrl(filename: string): string {
    return `${Constants.SERVER_URL}/download_report?file=${encodeURIComponent(filename)}`
  }

  /**
   * Fetch available billing periods (reuses the billing_periods component).
   */
  public static async getBillingPeriods(): Promise<any[]> {
    const res = await ReportsBroker.ax.get('/get?comp=billing_periods')
    return res ?? []
  }
}
